'use client';

import { useState, useEffect, useRef } from 'react';
import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import { ShoppingCart, User, Menu, CarFront, Heart, Building2, Bell, ChevronDown, Check, Tag, Zap, BookOpen, ShieldCheck, Truck, RotateCcw, Lock , Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWishlistStore } from '@/stores/wishlist';
import { Badge } from '@/components/ui/badge';
import { useCartStore } from '@/stores/cart';
import { useAuthStore } from '@/stores/auth';
import { useCustomerTypeStore } from '@/stores/customer-type';
import { useLocalePath } from '@/components/LocaleLink';
import { useRouter, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { CategoryNav } from '@/components/CategoryNav';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

const SearchBar = dynamic(
  () => import('@/components/SearchBar').then((m) => ({ default: m.SearchBar })),
  { loading: () => <Input type="search" placeholder="" disabled /> }
);

const MobileMenuPanel = dynamic(
  () => import('@/components/MobileMenuPanel').then((m) => ({ default: m.MobileMenuPanel })),
  { ssr: false }
);

const UserMenuDropdown = dynamic(
  () => import('@/components/UserMenuDropdown').then((m) => ({ default: m.UserMenuDropdown })),
  { ssr: false }
);
const ChangeVehicleDrawer = dynamic(
  () => import('@/components/ChangeVehicleDrawer').then((m) => ({ default: m.ChangeVehicleDrawer })),
  { ssr: false }
);
const VehicleSearchDropdown = dynamic(
  () => import('@/components/VehicleSearchDropdown').then((m) => ({ default: m.VehicleSearchDropdown })),
  { ssr: false }
);

export function Header({ freeShippingThreshold, bilarTillSalu = false }: { freeShippingThreshold: string; bilarTillSalu?: boolean }) {
  const itemCount = useCartStore((state) => state.getItemCount());
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const customer = useAuthStore((state) => state.customer);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const customerType = useCustomerTypeStore((state) => state.customerType);
  const hasChosenType = useCustomerTypeStore((state) => state.hasChosen);
  const setCustomerType = useCustomerTypeStore((state) => state.setCustomerType);
  const router = useRouter();
  const localePath = useLocalePath();
  const pathname = usePathname();
  const { locale } = useLocale();
  const threshold = freeShippingThreshold;
  const [mounted, setMounted] = useState(false);
  const antalFavoriter = useWishlistStore((s) => s.ids.size);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const [changeVehicleOpen, setChangeVehicleOpen] = useState(false);
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);


  const [unreadCount, setUnreadCount] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Measure header height for dropdown positioning
  useEffect(() => {
    if (!headerRef.current) return;
    const update = () => {
      const h = headerRef.current?.offsetHeight || 0;
      setHeaderHeight(h);
      // Fordonssidan fäster kategorichips och filterpanel strax under sidhuvudet.
      document.documentElement.style.setProperty('--af-header-h', `${h}px`);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [mounted, selectedVehicle]);

  // Load notification count when authenticated
  useEffect(() => {
    if (!mounted) return;
    if (isAuthenticated()) {
      import('@/lib/api').then(({ api }) => {
        api.getNotifications()
          .then((data) => setUnreadCount(data.unread_count))
          .catch(() => {});
      });
    }
  }, [mounted, isAuthenticated]);

  const handleLogout = async () => {
    logout();
    const { toast } = await import('sonner');
    toast.success(t(locale, 'header.loggedOut'));
    router.push(localePath('/'));
  };

  const katalogHref = selectedVehicle?.ktype
    ? `/fordon/${selectedVehicle.ktype}`
    : '/katalog';

  return (
    <header ref={headerRef} className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Förtroenderad — ENDAST desktop.
          Mobilvarianten är borttagen 2026-09-03: den kostade 30 av headerns
          185 px på 360×640 och upprepade löften som står i sidfoten och på
          produktsidan. Samma information, men inte överst på varje sida. */}
      <div className="hidden lg:block bg-brand text-white/90">
        <div className="container mx-auto px-4">
          {/* Varumärkets indigo — samma rad på varje sida (ägaren 2026-10-01: harmoni, kunder från hela Sverige). */}
          <div className="hidden lg:flex items-center gap-5 py-1.5 text-xs whitespace-nowrap overflow-hidden">
            <span className="hidden xl:flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-cream" />{t(locale, 'header.specialists')}</span>
            <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-cream" />{t(locale, 'header.deliverySweden')}</span>
            {/* Asterisken förklaras i samma rad. Fri frakt gäller inte långgods
                och pall, och löftet får inte stå oförbehållet någonstans
                (ägaren 2026-09-16: "det får inte gå att missförstå på något sätt"). */}
            <Link href="/frakt" className="flex items-center gap-1.5 hover:text-white">
              {t(locale, 'header.freeShipping', { threshold })}
              <span className="text-[11px] opacity-70">{t(locale, 'shipping.bulkyFootnote')}</span>
            </Link>
            <span className="flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5 text-cream" />{t(locale, 'header.returnPolicy')}</span>
            <a href="tel:042201620" className="ml-auto flex items-center gap-1.5 font-semibold text-white hover:underline">
              <Phone className="h-3.5 w-3.5 text-cream" />{t(locale, 'header.helpPhone')}: {t(locale, 'header.phone')}
            </a>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4">
        {/* Top bar */}
        <div className="flex h-16 items-center justify-between gap-2 md:gap-4">
          {/* Mobile menu trigger */}
          <Button variant="ghost" size="icon" onClick={() => { setMobileMenuOpen(true); setMobileMenuMounted(true); }} className="lg:hidden h-10 w-10">
            <Menu className="h-5 w-5" />
            <span className="sr-only">{t(locale, 'header.menu')}</span>
          </Button>

          {/* Mobile menu panel (lazy-loaded on first open, stays mounted for close animation) */}
          {mobileMenuMounted && <MobileMenuPanel open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} bilarTillSalu={bilarTillSalu} />}

          {/* Logo — capped på mobil så varukorgen får plats, och capped IGEN i
              spannet 1024-1279 px. Menyn (Katalog, Express, Erbjudanden, Bilar
              till salu, Guider, Företag) visas från lg, men logotypen släpptes
              fri samtidigt och blir då 327 px bred. Uppmätt 2026-09-08: den lade
              sig 96-111 px över ordet "Katalog" i hela det spannet. Släpps först
              vid xl, där allt får plats. */}
          <Link href="/" className="flex items-center min-w-0 shrink">
            <Image
              src="/logo.png"
              alt="Autofrance"
              width={327}
              height={40}
              className="h-7 sm:h-8 lg:h-10 w-auto max-w-[120px] sm:max-w-[180px] lg:max-w-[210px] xl:max-w-none"
              priority
            />
          </Link>

          {/* Actions — never shrink, cart must stay reachable */}
          <div className="flex items-center gap-1 shrink-0">
            <Link href={katalogHref} aria-label={t(locale, 'header.catalogue')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm">
                {t(locale, 'header.catalogue')}
              </Button>
            </Link>

            <Link href={selectedVehicle?.ktype ? `/express-leverans?ktype=${selectedVehicle.ktype}` : '/express-leverans'} aria-label={t(locale, 'header.express')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5 hover:text-brand">
                <Zap className="h-3.5 w-3.5" />
                {t(locale, 'header.express')}
              </Button>
            </Link>

            <Link href="/erbjudanden" aria-label={t(locale, 'header.deals')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5 font-semibold text-kop hover:text-kop hover:bg-cream">
                <Tag className="h-3.5 w-3.5" />
                {t(locale, 'header.deals')}
              </Button>
            </Link>

{bilarTillSalu && (
            <Link href="/bilar" aria-label={locale === 'sv' ? 'Bilar till salu' : 'Cars for sale'}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5 hover:text-brand">
                <CarFront className="h-3.5 w-3.5" />
                {locale === 'sv' ? 'Bilar till salu' : 'Cars for sale'}
              </Button>
            </Link>
            )}

            <Link href="/blogg" aria-label={t(locale, 'header.guides')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />
                {t(locale, 'header.guides')}
              </Button>
            </Link>

            <Link href="/foretag" aria-label={t(locale, 'header.business')}>
              <Button variant="ghost" size="sm" className="hidden lg:flex text-sm gap-1.5">
                <Building2 className="h-4 w-4" />
                {t(locale, 'header.business')}
              </Button>
            </Link>

            {mounted && isAuthenticated() ? (
              <UserMenuDropdown
                initial={customer?.firstname?.charAt(0) || 'K'}
                firstname={customer?.firstname}
                lastname={customer?.lastname}
                email={customer?.email}
                onLogout={handleLogout}
              />
            ) : (
              <Link href="/login" aria-label={t(locale, 'header.login')}>
                <Button variant="ghost" size="icon" className="hidden lg:flex" aria-label={t(locale, 'header.login')} tabIndex={-1}>
                  <User className="h-5 w-5" />
                </Button>
              </Link>
            )}

            {/* Fordonsväljare — ENDAST mobil.
                Satt tidigare i mobilblockets sökfält (onVehicleClick). När det
                blocket togs bort 2026-09-03 hade fordonsvalet försvunnit helt
                från mobilen, och /sok har ingen egen väljare. Vehicle-filtrerad
                trafik står för merparten av orderraderna, så knappen flyttade
                hit i stället — samma antal tryck, noll extra pixlar. */}
            <button
              onClick={() => setVehicleDropdownOpen(true)}
              className="lg:hidden flex items-center gap-1.5 h-10 px-2.5 rounded-md border text-xs font-medium max-w-[128px] hover:bg-accent transition-colors"
              aria-label={t(locale, 'search.selectVehicle')}
            >
              <CarFront className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate">
                {mounted && selectedVehicle?.manufacturer
                  ? selectedVehicle.manufacturer
                  : t(locale, 'search.selectVehicle')}
              </span>
            </button>

            {/* Favoriter. Pekade tidigare på /konto#onskeLista, som kräver
                inloggning — en återvändsgränd sedan listan fungerar utan
                konto. Hjärtat fylls rött och bär antalet så det syns att man
                sparat något; ägaren 2026-09-24: "den är inte röd heller,
                betyder inte att den är använd". */}
            <Link href="/favoriter" className="hidden lg:flex">
              <Button variant="ghost" size="icon" className="relative" aria-label={t(locale, 'header.wishlist')} tabIndex={-1}>
                <Heart className={`h-5 w-5 ${mounted && antalFavoriter > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                {mounted && antalFavoriter > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold
                                   rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                    {antalFavoriter > 99 ? '99+' : antalFavoriter}
                  </span>
                )}
              </Button>
            </Link>

            {/* Notifications bell */}
            {mounted && isAuthenticated() && (
              <Link href="/konto#notiser" className="hidden lg:flex">
                <Button variant="ghost" size="icon" className="relative" aria-label={t(locale, 'header.notifications')} tabIndex={-1}>
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </Link>
            )}

            {/* Customer type dropdown — only for anonymous visitors (desktop only, mobile has its own toggle below) */}
            {mounted && hasChosenType && !isAuthenticated() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden lg:flex items-center gap-1 h-8 px-2.5 rounded-md border text-xs font-medium hover:bg-accent transition-colors">
                    {customerType === 'private' ? t(locale, 'header.inclVat') : t(locale, 'header.exclVat')}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onClick={() => setCustomerType('private')}
                    className="flex items-start gap-3 py-2.5 cursor-pointer"
                  >
                    <User className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">{t(locale, 'header.private')}</span>
                      <p className="text-xs text-muted-foreground">{t(locale, 'header.privateSub')}</p>
                    </div>
                    {customerType === 'private' && <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCustomerType('business')}
                    className="flex items-start gap-3 py-2.5 cursor-pointer"
                  >
                    <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">{t(locale, 'header.businessType')}</span>
                      <p className="text-xs text-muted-foreground">{t(locale, 'header.businessSub')}</p>
                    </div>
                    {customerType === 'business' && <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Varukorgen — ENDAST desktop. Bottennaven har en egen
                varukorgsikon med samma räknare på mobil, så headerns var en
                dubblett av något som redan syns permanent längst ned. */}
            <Button
              variant="ghost"
              size="icon"
              className="relative hidden lg:flex h-10 w-10 shrink-0 bg-kop hover:bg-kop/90 text-white hover:text-white"
              aria-label={t(locale, 'header.cart')}
              onClick={() => {
                if (pathname === '/varukorg') {
                  router.back();
                } else {
                  router.push('/varukorg');
                }
              }}
            >
              <ShoppingCart className="h-5 w-5" />
              {mounted && itemCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center px-1 py-0 text-[10px] font-bold"
                >
                  {itemCount > 99 ? '99+' : itemCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Search row (desktop) — search + vehicle picker merged into one control */}
        <div className="hidden lg:flex items-center gap-3 py-2.5 border-t">
          <div className="flex-1">
            <SearchBar onVehicleClick={() => setVehicleDropdownOpen(true)} />
          </div>
          {/* De färgade märkena (grönt, blått, gult) bredvid sökrutan är borttagna: samma löften står i
              raden överst. Erbjudandet nås via Erbjudanden i menyn. */}
        </div>

        {/* ChangeVehicleDrawer (opened from the vehicle picker) */}
        {changeVehicleOpen && (
          <ChangeVehicleDrawer open={changeVehicleOpen} onClose={() => setChangeVehicleOpen(false)} />
        )}

        {/* Mobile search + vehicle picker — merged into one control:
            the tappable car chip inside the search box opens the vehicle selector. */}
        {/* Mobilblocket (sökfält + Kategorier + momsväxlare) är borttaget
            2026-09-03. Det kostade 90 av headerns 185 px på 360×640 och allt i
            det fanns någon annanstans: Sök och Katalog i bottennaven,
            momsväxlaren i hamburgarmenyn (MobileMenuPanel, samma villkor).
            Fordonsväljaren satt dock BARA här — den flyttade till huvudraden
            ovan som en kompakt knapp, eftersom vehicle-filtrerad trafik är den
            starkaste säljkanalen och /sok saknar fordonsväljare. */}

        {/* Vehicle search dropdown */}
        {vehicleDropdownOpen && (
          <VehicleSearchDropdown
            open={vehicleDropdownOpen}
            onClose={() => setVehicleDropdownOpen(false)}
            headerHeight={headerHeight}
          />
        )}
      </div>
      <CategoryNav />
    </header>
  );
}
